export function safeParseFloat(input: string | number | undefined | null): number {
  if (input === undefined || input === null) return 0
  if (typeof input === 'number') return input
  const cleaned = input.replace(/,/g, '')
  const parsed = parseFloat(cleaned)
  return isNaN(parsed) ? 0 : parsed
}

export function formatFirstLetterToUpperCase(text?: string) {
  if (!text) return ''
  return text.charAt(0).toUpperCase() + text.slice(1).toLowerCase()
}

export function formatCurrency(input?: number | string, exact: boolean = false) {
  const value = typeof input === 'string' ? safeParseFloat(input) : input
  if (value === undefined || value === null || isNaN(Number(value))) return '-'

  const numValue = Number(value)

  if (exact) {
    return numValue.toLocaleString('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
    })
  }

  if (numValue > 1_000_000_000) {
    return `$${(numValue / 1_000_000_000).toFixed(2)}B`
  } else if (numValue > 1_000_000) {
    return `$${(numValue / 1_000_000).toFixed(2)}M`
  } else if (numValue > 1_000) {
    return `$${(numValue / 1_000).toFixed(2)}K`
  } else {
    return numValue.toLocaleString('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
    })
  }
}

export function formatTime(time: number) {
  const totalSeconds = Math.floor(time / 1000)
  const days = Math.floor(totalSeconds / 86400)
  const hours = Math.floor((totalSeconds % 86400) / 3600)
  const minutes = Math.floor((totalSeconds % 3600) / 60)
  const seconds = totalSeconds % 60

  if (days > 0) {
    return hours > 0 ? `${days}d ${hours}h` : `${days}d`
  }

  if (hours > 0) {
    return minutes > 0 ? `${hours}h ${minutes}min` : `${hours}h`
  }

  if (minutes > 0) {
    return seconds > 0 ? `${minutes}min ${seconds}sec` : `${minutes}min`
  }

  return `${seconds}sec`
}

const SPOT_SYMBOL_SUFFIX = '-SPOT'

/**
 * Hyperliquid spot tokens bridged in by Unit carry a `U` prefix (UETH, UBTC) that is an
 * implementation detail of the bridge, not a different asset. Plain U-tokens (USDC, USDE,
 * USDT0, UNI) are real assets and are deliberately absent. A few map to the name the rest
 * of the market uses rather than the bare remainder (UFART → FARTCOIN).
 */
const BRIDGED_ASSETS: Record<string, string> = {
  UAVAX: 'AVAX',
  UBONK: 'BONK',
  UBTC: 'BTC',
  UDZ: '2Z',
  UENA: 'ENA',
  UETH: 'ETH',
  UFART: 'FARTCOIN',
  UMEGA: 'MEGA',
  UMOG: 'MOG',
  UMON: 'MON',
  UPUMP: 'PUMP',
  USOL: 'SOL',
  UVIRT: 'VIRTUAL',
  UWLD: 'WLD',
  UXPL: 'XPL',
  UZEC: 'ZEC',
}

/**
 * Display-only instrument symbol. Some venues (Bybit) tag spot symbols with a `-SPOT` suffix
 * (`BTCUSDT-SPOT`) to keep them apart from the same-named perp; the backend needs it, traders
 * don't. Hyperliquid's Unit-bridged spot bases lose their `U` the same way (UETH-USDC →
 * ETH-USDC). Never feed the result back into API calls, lookups, or keys — use the raw symbol there.
 */
export function formatSymbol<T extends string | null | undefined>(symbol: T): T {
  if (!symbol) return symbol
  const unsuffixed = symbol.endsWith(SPOT_SYMBOL_SUFFIX)
    ? symbol.slice(0, -SPOT_SYMBOL_SUFFIX.length)
    : symbol
  const dash = unsuffixed.indexOf('-')
  const base = dash === -1 ? unsuffixed : unsuffixed.slice(0, dash)
  const unwrapped = BRIDGED_ASSETS[base]
  return (unwrapped ? unwrapped + unsuffixed.slice(base.length) : unsuffixed) as T
}

/** The underlying asset of a bridged token (UETH → ETH); anything else passes through uppercased. */
export function unwrapBridgedAsset(asset: string): string {
  const upper = asset.toUpperCase()
  return BRIDGED_ASSETS[upper] ?? upper
}

export function formatCryptoSymbol(symbol: string) {
  return symbol.split('_').join('/')
}
